import React, { useEffect, useState } from "react";
import {
  useDispositionQuery,
  useGetDispositionStatusQuery,
} from "../../Services/ticket/CreateTicket";
import { USER_TEAM_ID } from "../../constants";
import { CloseButton } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Loader } from "react-bootstrap-typeahead";
import ReactModal from "react-modal";
import WorkflowModal from "./WorkflowModal";
import { Box, IconButton, Modal, Typography } from "@mui/material";
import { GridCloseIcon } from "@mui/x-data-grid";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";

interface IPROPS {
  closeModal: any;
  storedDisposition: any;
  refetch: any;
  trigger: any;
  isLoading: any;
  ticketId: any;
  otherMailDetails: any;
  cc: any;
  bcc: any;
  emails: any;
  editorHtml: any;
  files: any;
  templateAttachment: any;
  setCheckingForReplyApi: any;
  setLiftingReplyLoader: any;
  setShowReplyCard: any;
}

interface FetchError {
  status: number;
  data: {
    disposition?: string[];
  };
}

const DispositionModal: React.FC<IPROPS> = ({
  closeModal,
  storedDisposition,
  refetch,
  trigger,
  isLoading,
  ticketId,
  otherMailDetails,
  cc,
  bcc,
  emails,
  editorHtml,
  files,
  templateAttachment,
  setCheckingForReplyApi,
  setLiftingReplyLoader,
  setShowReplyCard,
}) => {
  const getTeamId = localStorage.getItem(USER_TEAM_ID);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [lastSelectedId, setLastSelectedId] = useState<number | null>(null);
  const [selectedId, setSelectedId] = useState("");
  const [checkForValue, setCheckForValue] = useState(false);
  const [isEditDispositionTrue, setIsEditDispositionTrue] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [checkValue, setCheckValue] = useState<any>([]);
  const [skipDispositionQuery, setSkipDispositionQuery] =
    useState<boolean>(true);
  const [showAdditionalField, setShowAdditionalField] = useState(false);
  const [ticketName, setTicketName] = useState<any>("");
  const [workflowModalClicked, setWorkflowModalClicked] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(false); // New state for controlling API call

  const [dispoError, setDispoError] = useState<FetchError>(null);

  const isFetchBaseQueryError = (
    error: unknown
  ): error is FetchBaseQueryError => {
    return (error as FetchBaseQueryError)?.data !== undefined;
  };

  const closeStatusModal = () => {
    setStatusModalOpen(false);
    closeModal();
  };
  const { data: Disposition } = useDispositionQuery(getTeamId);

  const {
    data: fetchDispositionStatus,
    error: fetchDispositionError,
    status: fetchDispositionStats,
  } = useGetDispositionStatusQuery(lastSelectedId, {
    skip: !shouldFetch,
  });

  useEffect(() => {
    if (fetchDispositionError && isFetchBaseQueryError(fetchDispositionError)) {
      setDispoError(fetchDispositionError as FetchError);
    }
  }, [fetchDispositionError]);

  const [options, setOptions] = useState(Disposition || []);
  const { uuid } = useParams();

  useEffect(() => {
    if (Disposition && storedDisposition) {
      let initialSelectedIds: number[] = [];
      let currentOptions = Disposition;
      let finalLastId = null;

      storedDisposition.forEach((storedValue: string) => {
        const matchingOption = currentOptions.find(
          (option) => option.topic === storedValue
        );
        if (matchingOption) {
          initialSelectedIds.push(matchingOption.id);
          finalLastId = matchingOption.id; // Track the last valid ID
          currentOptions = matchingOption.sub_topics || [];
        } else {
          initialSelectedIds.push(-1);
        }
      });

      setSelectedIds(initialSelectedIds);
      setOptions(currentOptions);

      // Set initial last selected ID
      if (finalLastId !== null) {
        setLastSelectedId(finalLastId);

        // Check if we're at the last level
        const hasNoSubTopics =
          !currentOptions ||
          currentOptions.length === 0 ||
          currentOptions.every(
            (option) => !option.sub_topics || option.sub_topics.length === 0
          );
        setCheckForValue(hasNoSubTopics);
      }
    }
  }, [Disposition, storedDisposition]);

  useEffect(() => {
    // Check if we're at the last level of the hierarchy
    if (options && options.length > 0) {
      const hasNoSubTopics = options.every(
        (option) => !option.sub_topics || option.sub_topics.length === 0
      );
      setCheckForValue(hasNoSubTopics);
    } else {
      setCheckForValue(true); // No more options means we're at the end
    }
  }, [options]);

  // Function to find the specific disposition node by ID
  const findDispositionById = (data, id) => {
    if (!data || !id) return null;

    for (const item of data) {
      if (item.id === id) {
        return item;
      }

      if (item.sub_topics && item.sub_topics.length > 0) {
        const found = findDispositionById(item.sub_topics, id);
        if (found) {
          return found;
        }
      }
    }

    return null;
  };

  const renderDropdowns = (data, level) => {
    if (!data || data.length === 0) {
      return null;
    }

    return (
      <div className="disposition_modal_cont">
        {level > 0 && <label className=" required mt-4">Sub Disposition</label>}
        <select
          name={`Level${level}`}
          className="form-select form-select-lg form-select-solid"
          onChange={handleChange}
          value={selectedIds[level] || ""}
        >
          <option value="" disabled>
            Select
          </option>
          {data?.map((option) => (
            <option key={option.id} value={option.id}>
              {option.topic}
            </option>
          ))}
        </select>
        {selectedIds[level] &&
          renderDropdowns(
            data.find((item) => item.id === selectedIds[level])?.sub_topics ||
              [],
            level + 1
          )}
      </div>
    );
  };

  // Updated function to properly handle disposition selection
  useEffect(() => {
    if (lastSelectedId && Disposition) {
      // Find the exact disposition item based on lastSelectedId
      const dispositionItem = findDispositionById(Disposition, lastSelectedId);

      if (dispositionItem) {
        setTicketName(dispositionItem.ticket_name);

        if (
          dispositionItem.sub_topics &&
          dispositionItem.sub_topics.length > 0
        ) {
          setCheckValue(dispositionItem.sub_topics);
          setSkipDispositionQuery(true);
          setShowAdditionalField(false);
        } else {
          setSkipDispositionQuery(false);
          setShowAdditionalField(true);
        }
      }
    }
  }, [lastSelectedId, Disposition]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const selectedValue = parseInt(value);
    setSelectedId(value);

    const level = parseInt(name.replace("Level", ""));

    const newSelectedIds = [...selectedIds];
    newSelectedIds[level] = selectedValue;
    newSelectedIds.length = level + 1; // Remove any deeper selections
    setSelectedIds(newSelectedIds);
    setLastSelectedId(selectedValue);

    // Update options for the next level dropdown
    let currentOptions = Disposition;
    for (let i = 0; i <= level; i++) {
      const selectedId = i < level ? newSelectedIds[i] : selectedValue;
      if (selectedId !== undefined) {
        const found = currentOptions.find((item) => item.id === selectedId);
        if (found && i === level) {
          // We're at the current selection level
          setOptions(found.sub_topics || []);

          // Update ticket info and additional field visibility
          setTicketName(found.ticket_name);

          if (found.sub_topics && found.sub_topics.length > 0) {
            setCheckValue(found.sub_topics);
            setSkipDispositionQuery(true);
            setShowAdditionalField(false);
          } else {
            setSkipDispositionQuery(false);
            setShowAdditionalField(true);
          }
          break;
        } else if (found) {
          // We're traversing to the selected level
          currentOptions = found.sub_topics || [];
        } else {
          currentOptions = [];
          break;
        }
      }
    }
  };

  const isAllLevelsSelected = () => {
    return selectedIds.every((id) => id !== undefined && id !== null);
  };

  const editHandler = async (e) => {
    e.preventDefault();

    // Ensure we have a valid selection
    if (!lastSelectedId) {
      toast.error("Please select a disposition");
      return;
    }

    setShouldFetch(true);
    setWorkflowModalClicked(true);
  };

  useEffect(() => {
    if (fetchDispositionStats === "rejected") {
      const errorMessage =
        dispoError?.data?.disposition?.[0] ||
        "No ticket name for the selected disposition";
      toast.error(errorMessage);

      setWorkflowModalClicked(false);
      setShouldFetch(false);
    } else if (fetchDispositionStats === "fulfilled" && workflowModalClicked) {
      setStatusModalOpen(true);
    }
  }, [fetchDispositionStats, dispoError]);

  useEffect(() => {
    const fetchData = async () => {
      if (workflowModalClicked && fetchDispositionStats === "fulfilled") {
        // Always use lastSelectedId as it's been properly set
        if (!lastSelectedId) {
          toast.error("Kindly fill all the disposition");
          setWorkflowModalClicked(false);
          return;
        }

        const requestBody = { disposition: lastSelectedId };

        try {
          const response = await trigger({
            body: requestBody,
            id: uuid,
          });
          if (response?.data) {
            await refetch();
          } else if (response?.error) {
            const errorData = (response?.error as any)?.data?.detail?.[0];
            toast.error(errorData || "Unable to edit");
          }
        } catch (error) {
          toast.error("An error occurred");
        }
      }
    };

    fetchData(); // Call the async function
  }, [
    workflowModalClicked,
    fetchDispositionStats,
    Disposition,
    lastSelectedId,
    uuid,
    trigger,
    refetch,
  ]);

  const isLastDropdownSelected =
    selectedIds.length > 0 && selectedIds[selectedIds.length - 1] !== undefined;

  const editDispositionHandler = () => {
    setIsEditDispositionTrue(!isEditDispositionTrue);
  };

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "40%",
    overflow: "hidden",
    bgcolor: "background.paper",
    border: "1px solid transparent",
    boxShadow: 2,
    borderRadius: "5px",
    p: 4,
    maxHeight: "100%",
    paddingBottom: "10px",
  };

  return (
    <div>
      <div
        className="modal-header"
        style={{
          paddingLeft: "5px",
          paddingRight: "0px",
          paddingTop: "5px",
          borderBottom: "0px",
        }}
      >
        <h2>Dispositions</h2>
        <div
          style={{
            display: "flex",
            gap: "10px",
            alignItems: "center",
          }}
        >
          <CloseButton onClick={closeModal} />
        </div>
      </div>
      <div>
        <label className="required">Disposition</label>

        {renderDropdowns(Disposition, 0)}
      </div>
      <div
        style={{
          marginTop: "10px",
          display: "flex",
          justifyContent: "end",
        }}
      >
        <button
          className="btn btn-primary"
          onClick={editHandler}
          disabled={isLoading}
          style={{
            cursor: isLoading ? "not-allowed" : "pointer",
          }}
        >
          {isLoading ? (
            <>
              Saving...
              <Loader />
            </>
          ) : (
            "Save"
          )}
        </button>
      </div>

      <Modal
        open={statusModalOpen}
        onClose={closeStatusModal}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <Box sx={{ ...style, position: "absolute" }}>
          <IconButton
            aria-label="close"
            onClick={closeModal}
            sx={{
              position: "fixed",
              top: 16,
              right: 16,
              zIndex: 1301,
              background: "white",
            }}
          >
            <GridCloseIcon />
          </IconButton>
          <Typography
            id="modal-title"
            variant="h6"
            component="h2"
            sx={{ mb: 2 }}
            style={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            Workflow
          </Typography>

          <Box>
            <WorkflowModal
              closeModal={closeStatusModal}
              ticketId={ticketId}
              lastSelectedId={lastSelectedId}
              ticketName={ticketName}
              fetchDispositionStatus={fetchDispositionStatus}
              refetch={refetch}
              otherMailDetails={otherMailDetails}
              cc={cc}
              bcc={bcc}
              emails={emails}
              editorHtml={editorHtml}
              files={files}
              templateAttachment={templateAttachment}
              setCheckingForReplyApi={setCheckingForReplyApi}
              setLiftingReplyLoader={setLiftingReplyLoader}
              setShowReplyCard={setShowReplyCard}
            />
          </Box>
        </Box>
      </Modal>
    </div>
  );
};

export default DispositionModal;